import { ComponentProps, Fragment, ReactNode } from 'react'

import { Link, LinkProps } from '@tanstack/react-router'
import { House } from 'lucide-react'

import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  Breadcrumb as BreadcrumbPrimitive,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'

import { cn } from '@/lib/cn'

function AdminPageRoot({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      {...props}
      className={cn('relative flex flex-1 flex-col gap-6 px-4 py-4', className)}
    />
  )
}

interface AdminPageBreadcrumbProps {
  items: [label: string | ReactNode, to: LinkProps['to']][]
}

function Breadcrumb({ items }: AdminPageBreadcrumbProps) {
  const _items = [[<House size={16} />, '/admin-v2/participant'], ...items] as [
    label: string | ReactNode,
    to: LinkProps['to'],
  ][]

  const len = _items.length

  return (
    <BreadcrumbPrimitive className="text-white">
      <BreadcrumbList className="text-[#475467]">
        {_items.map(([label, to], idx) => {
          const isLast = idx === len - 1

          return (
            <Fragment key={idx}>
              {!isLast ? (
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={to}>{label}</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              ) : (
                <BreadcrumbItem>
                  <BreadcrumbPage className="rounded-md bg-[#FFF4F5] px-1 py-0.5 font-medium text-primary-yes">
                    {label}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              )}
              {!isLast && (
                <BreadcrumbSeparator className="-rotate-12 text-[#475467]" />
              )}
            </Fragment>
          )
        })}
      </BreadcrumbList>
    </BreadcrumbPrimitive>
  )
}

interface HeaderProps extends Omit<ComponentProps<'div'>, 'title'> {
  title: string
  desc?: ReactNode
  action?: ReactNode
}

function Header({ title, desc, className, action, ...props }: HeaderProps) {
  return (
    <div className={cn('flex', className)} {...props}>
      <div className="flex-1">
        <h1 className="text-lg font-semibold">{title}</h1>
        {desc ? (
          typeof desc === 'string' ? (
            <p className="text-sm text-muted-foreground">{desc}</p>
          ) : (
            desc
          )
        ) : null}
      </div>
      {action && <div className="flex items-center gap-2">{action}</div>}
    </div>
  )
}

export const AdminPage = Object.assign(AdminPageRoot, {
  Breadcrumb,
  Header,
})
